import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { PlusButton } from "@/components/design/PlusButton";

// Freie Schlagworte; Aufrufer können vorhandene Katalogwerte als Vorschläge anbieten.
//
// Chips im Stil von `.smycles-chip-button`, nur nicht als Knopf, weil ein Tag
// nichts auslöst; das × daneben ist der Knopf. Neue Tags kommen über ein Feld,
// das erst auf Anforderung erscheint - eine leere Eingabezeile in einer sonst
// leeren Box wäre die lauteste Stelle des Screens.
//
// Das Eingabefeld trägt dieselbe Pillenform wie die Chips daneben, statt als
// Kasten unter ihnen zu stehen: man schreibt sichtbar in die Form, die gleich
// entsteht, und die Zeile springt beim Abschicken nicht um.

export function ProjectTags({
  tags,
  onChange,
  readOnly = false,
  emptyHint,
  suggestions = [],
}: {
  tags: readonly string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
  emptyHint?: ReactNode;
  suggestions?: readonly string[];
}) {
  const intl = useIntl();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  function add(next = draft, keepOpen = false) {
    const value = next.trim();
    // Doppelte Tags sind kein Fehler, den man dem Nutzer melden muss - sie
    // werden einfach nicht zweimal aufgenommen.
    if (value && !tags.includes(value)) onChange([...tags, value]);
    setDraft("");
    setAdding(keepOpen);
  }

  return (
    <div className="flex flex-wrap items-center gap-2" onBlur={event => {
      if (adding && !event.currentTarget.contains(event.relatedTarget as Node | null)) add();
    }}>
      {/* Ein Tag ist nur sein Text, und der taugt nicht als id; gezählt wird deshalb
          innerhalb der Liste, der Inhalt wird am Anker geprüft. */}
      {tags.map((tag, i) => (
        <span
          key={tag}
          id={`project-tags-item-${i}`}
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-smycles-label text-ink ring-1 ring-ink/15"
        >
          {tag}
          {readOnly ? null : (
            <button
              id={`project-tags-remove-${i}`}
              type="button"
              className="text-ink-muted transition hover:text-ink"
              aria-label={intl.formatMessage(
                { id: "project.tags.remove", defaultMessage: "{tag} entfernen" },
                { tag },
              )}
              onClick={() => onChange(tags.filter((t) => t !== tag))}
            >
              ×
            </button>
          )}
        </span>
      ))}

      {readOnly ? (
        tags.length === 0 ? <span className="smycles-field-meta text-ink-muted">–</span> : null
      ) : adding ? (
        <span className="inline-flex items-center rounded-full px-3 py-1.5 text-smycles-label ring-1 ring-ink-ui">
          <input
            id="project-tags-input"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add(draft, true);
              }
              if (e.key === "Escape") {
                setDraft("");
                setAdding(false);
              }
            }}
            aria-label={intl.formatMessage({ id: "project.tags.new", defaultMessage: "Neues Tag" })}
            placeholder={intl.formatMessage({
              id: "project.tags.placeholder",
              defaultMessage: "Tag und Enter",
            })}
            // Wächst mit dem Text, startet aber breit genug für ein kurzes Wort.
            className="min-w-[9ch] max-w-[24ch] bg-transparent text-smycles-label text-ink outline-none placeholder:text-ink-muted [field-sizing:content]"
          />
        </span>
      ) : (
        <PlusButton
          id="project-tags-add"
          label={intl.formatMessage({ id: "project.tags.add", defaultMessage: "Tag" })}
          onClick={() => setAdding(true)}
        />
      )}

      {adding && suggestions.length ? <div className="flex w-full flex-wrap gap-2">
        {[...new Set(suggestions)].filter(value => !tags.includes(value) && value.toLocaleLowerCase(intl.locale).includes(draft.trim().toLocaleLowerCase(intl.locale))).map((value, index) => (
          <Button key={value} id={`project-tags-suggestion-${index}`} variant="secondary" size="compact"
            onMouseDown={event => event.preventDefault()} onClick={() => add(value)}>{value}</Button>
        ))}
      </div> : null}

      {tags.length === 0 && !adding && !readOnly ? (
        <span className="smycles-field-meta text-ink-muted">
          {emptyHint ?? <FormattedMessage
            id="project.tags.empty"
            defaultMessage="Noch keine Tags. Sie helfen später beim Finden ähnlicher Projekte."
          />}
        </span>
      ) : null}
    </div>
  );
}
